import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tag, Plus, Save, X, Play, Pause, Ban, Trash2, Edit2, History, Ticket, Eye } from "lucide-react";
import { toast } from "sonner";

const OBJECTIVES = [
  { value: "captacion", label: "Captación" },
  { value: "fidelizacion", label: "Fidelización" },
  { value: "recuperacion", label: "Recuperación (clientes que cancelaron)" },
  { value: "renovacion", label: "Renovación anticipada" },
  { value: "upgrade", label: "Upgrade (cambio a plan superior)" },
  { value: "convenio", label: "Convenio" },
  { value: "cortesia", label: "Cortesía" },
  { value: "campana_especial", label: "Campaña Especial" },
];

const BENEFITS = [
  { value: "percentage", label: "Descuento por porcentaje (1% - 100%)" },
  { value: "fixed_amount", label: "Descuento por valor fijo ($)" },
  { value: "special_price", label: "Precio promocional especial" },
  { value: "free_months", label: "Meses adicionales gratis" },
  { value: "two_for_one", label: "Promoción 2x1" },
  { value: "free_subscription", label: "Suscripción 100% gratuita" },
];

const AUDIENCES = [
  { value: "all", label: "Todos los usuarios" },
  { value: "new", label: "Nuevos clientes" },
  { value: "active", label: "Clientes actuales (activos)" },
  { value: "inactive", label: "Clientes inactivos" },
  { value: "cancelled", label: "Clientes que cancelaron" },
  { value: "specific", label: "Usuarios específicos" },
];

const STATUS_STYLES = {
  active: "bg-green-500/20 text-green-400",
  paused: "bg-amber-500/20 text-amber-300",
  scheduled: "bg-blue-500/20 text-blue-300",
  draft: "bg-white/10 text-white/60",
  disabled: "bg-red-500/20 text-red-300",
};

const emptyForm = {
  name: "",
  objective: "captacion",
  benefit_type: "percentage",
  discount_percentage: 20,
  fixed_amount: 0,
  special_price_monthly: "",
  special_price_annual: "",
  free_months: 1,
  free_mode: "months",
  free_days: 30,
  free_until: "",
  applies_to_plans: ["monthly", "annual"],
  audience: "all",
  specific_user_ids: [],
  coupon_code: "",
  requires_code: false,
  starts_at: "",
  ends_at: "",
  max_uses: 0,
  max_uses_per_user: 1,
  combinable: false,
  status: "draft",
  show_countdown: false,
  banner_title: "",
  banner_description: "",
  notes: "",
};

const toPayload = (f) => ({
  ...f,
  discount_percentage: parseInt(f.discount_percentage) || 0,
  fixed_amount: parseFloat(f.fixed_amount) || 0,
  special_price_monthly: f.special_price_monthly === "" ? null : parseFloat(f.special_price_monthly),
  special_price_annual: f.special_price_annual === "" ? null : parseFloat(f.special_price_annual),
  free_months: parseInt(f.free_months) || 0,
  free_days: parseInt(f.free_days) || 0,
  free_until: f.free_until ? new Date(f.free_until).toISOString() : null,
  starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null,
  ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null,
  max_uses: parseInt(f.max_uses) || 0,
  max_uses_per_user: parseInt(f.max_uses_per_user) || 1,
  coupon_code: f.coupon_code || null,
});

const toLocalInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");

export const PromotionsModule = () => {
  const { API } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!showForm) return;
    const t = setTimeout(() => loadPreview(), 400);
    return () => clearTimeout(t);
  }, [form, showForm]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, u] = await Promise.all([
        axios.get(`${API}/admin/promo-campaigns`),
        axios.get(`${API}/admin/promo-audit`),
        axios.get(`${API}/admin/subscriptions`),
      ]);
      setCampaigns(c.data);
      setAudit(a.data);
      setUsers(u.data);
    } catch (e) {
      toast.error("Error al cargar promociones");
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await axios.post(`${API}/admin/promo-campaigns/preview`, toPayload(form));
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePlan = (plan) => {
    setForm((f) => ({
      ...f,
      applies_to_plans: f.applies_to_plans.includes(plan)
        ? f.applies_to_plans.filter((p) => p !== plan)
        : [...f.applies_to_plans, plan],
    }));
  };

  const startNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (c) => {
    setForm({
      ...emptyForm,
      ...c,
      special_price_monthly: c.special_price_monthly ?? "",
      special_price_annual: c.special_price_annual ?? "",
      coupon_code: c.coupon_code || "",
      free_until: toLocalInput(c.free_until),
      starts_at: toLocalInput(c.starts_at),
      ends_at: toLocalInput(c.ends_at),
    });
    setEditingId(c.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ponle un nombre a la promoción");
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/promo-campaigns/${editingId}`, toPayload(form));
        toast.success("Promoción actualizada");
      } else {
        await axios.post(`${API}/admin/promo-campaigns`, toPayload(form));
        toast.success("Promoción creada");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la promoción");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (c, status) => {
    try {
      await axios.post(`${API}/admin/promo-campaigns/${c.id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar el estado");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar la promoción "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-campaigns/${c.id}`);
      toast.success("Promoción eliminada");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const bt = form.benefit_type;

  return (
    <div className="space-y-6" data-testid="promotions-module">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="w-6 h-6 text-[#E50914]" />
            Promociones
          </h2>
          <p className="text-white/50 text-sm">
            Crea descuentos, cupones, meses gratis y accesos gratuitos sin tocar código.
          </p>
        </div>
        {!showForm && (
          <Button onClick={startNew} className="bg-[#E50914] hover:bg-[#E50914]/90 text-white" data-testid="new-promo-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva promoción
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <Card className="bg-card/50 backdrop-blur-md border-white/10" data-testid="promo-form">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">{editingId ? "Editar promoción" : "Nueva promoción"}</CardTitle>
              <CardDescription className="text-white/60">3 pasos: qué ofreces, a quién y cuándo.</CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="promo-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Paso 1 */}
            <div className="space-y-4">
              <p className="text-[#E50914] text-sm font-bold uppercase tracking-wider">Paso 1 · Qué ofreces</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Nombre de la promoción</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Ej: Black Friday 40%"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="promo-name-input"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Objetivo de la promoción</Label>
                  <Select value={form.objective} onValueChange={(v) => set("objective", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="promo-objective-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECTIVES.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block">Tipo de beneficio</Label>
                <Select value={form.benefit_type} onValueChange={(v) => set("benefit_type", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="promo-benefit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BENEFITS.map((b) => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Campos según beneficio */}
              <div className="grid md:grid-cols-3 gap-4">
                {bt === "percentage" && (
                  <div>
                    <Label className="text-white mb-2 block">Porcentaje de descuento (%)</Label>
                    <Input
                      type="number" min="1" max="100"
                      value={form.discount_percentage}
                      onChange={(e) => set("discount_percentage", e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="promo-percentage-input"
                    />
                  </div>
                )}
                {bt === "fixed_amount" && (
                  <div>
                    <Label className="text-white mb-2 block">Descuento en dólares ($)</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={form.fixed_amount}
                      onChange={(e) => set("fixed_amount", e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="promo-fixed-input"
                    />
                  </div>
                )}
                {bt === "special_price" && (
                  <>
                    <div>
                      <Label className="text-white mb-2 block">Precio especial Mensual ($)</Label>
                      <Input
                        type="number" min="0" step="0.01" placeholder="Normal: 10"
                        value={form.special_price_monthly}
                        onChange={(e) => set("special_price_monthly", e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="promo-special-monthly-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Precio especial Anual ($)</Label>
                      <Input
                        type="number" min="0" step="0.01" placeholder="Normal: 100"
                        value={form.special_price_annual}
                        onChange={(e) => set("special_price_annual", e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="promo-special-annual-input"
                      />
                    </div>
                  </>
                )}
                {bt === "free_months" && (
                  <div>
                    <Label className="text-white mb-2 block">Meses adicionales gratis</Label>
                    <Input
                      type="number" min="1"
                      value={form.free_months}
                      onChange={(e) => set("free_months", e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="promo-free-months-input"
                    />
                  </div>
                )}
                {bt === "free_subscription" && (
                  <>
                    <div>
                      <Label className="text-white mb-2 block">Duración del acceso gratis</Label>
                      <Select value={form.free_mode} onValueChange={(v) => set("free_mode", v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="promo-free-mode-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="days">Por días</SelectItem>
                          <SelectItem value="months">Por meses</SelectItem>
                          <SelectItem value="dates">Hasta una fecha</SelectItem>
                          <SelectItem value="permanent">Permanente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.free_mode === "days" && (
                      <div>
                        <Label className="text-white mb-2 block">Días gratis</Label>
                        <Input type="number" min="1" value={form.free_days}
                          onChange={(e) => set("free_days", e.target.value)}
                          className="bg-white/5 border-white/10 text-white" data-testid="promo-free-days-input" />
                      </div>
                    )}
                    {form.free_mode === "months" && (
                      <div>
                        <Label className="text-white mb-2 block">Meses gratis</Label>
                        <Input type="number" min="1" value={form.free_months}
                          onChange={(e) => set("free_months", e.target.value)}
                          className="bg-white/5 border-white/10 text-white" data-testid="promo-free-months-2-input" />
                      </div>
                    )}
                    {form.free_mode === "dates" && (
                      <div>
                        <Label className="text-white mb-2 block">Gratis hasta</Label>
                        <Input type="datetime-local" value={form.free_until}
                          onChange={(e) => set("free_until", e.target.value)}
                          className="bg-white/5 border-white/10 text-white" data-testid="promo-free-until-input" />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <Label className="text-white mb-2 block">Planes a los que aplica</Label>
                <div className="flex gap-3">
                  {[{ v: "monthly", l: "Mensual $10" }, { v: "annual", l: "Anual $100" }].map((p) => (
                    <Button
                      key={p.v}
                      type="button"
                      onClick={() => togglePlan(p.v)}
                      variant={form.applies_to_plans.includes(p.v) ? "default" : "outline"}
                      className={form.applies_to_plans.includes(p.v)
                        ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                        : "border-white/20 text-white hover:bg-white/10"}
                      data-testid={`promo-plan-${p.v}`}
                    >
                      {p.l}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Paso 2 */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <p className="text-[#E50914] text-sm font-bold uppercase tracking-wider">Paso 2 · A quién aplica</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Público objetivo</Label>
                  <Select value={form.audience} onValueChange={(v) => set("audience", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="promo-audience-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AUDIENCES.map((a) => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.requires_code}
                      onCheckedChange={(v) => set("requires_code", v)}
                      data-testid="promo-requires-code-switch"
                    />
                    <span className="text-white text-sm">Requiere código / cupón</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.combinable}
                      onCheckedChange={(v) => set("combinable", v)}
                      data-testid="promo-combinable-switch"
                    />
                    <span className="text-white text-sm">Combinable con otras</span>
                  </div>
                </div>
              </div>

              {form.audience === "specific" && (
                <div className="max-h-56 overflow-y-auto bg-white/5 rounded-lg border border-white/10 p-3 space-y-1" data-testid="promo-users-list">
                  {users.map((u) => {
                    const checked = form.specific_user_ids.includes(u.user_id);
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        onClick={() =>
                          set("specific_user_ids", checked
                            ? form.specific_user_ids.filter((id) => id !== u.user_id)
                            : [...form.specific_user_ids, u.user_id])
                        }
                        className={`w-full text-left px-3 py-2 rounded ${checked ? "bg-[#E50914]/20 text-white" : "text-white/60 hover:bg-white/5"}`}
                        data-testid={`promo-user-option-${u.user_id}`}
                      >
                        {u.full_name || "Usuario"} — {u.email}
                      </button>
                    );
                  })}
                </div>
              )}

              {form.requires_code && (
                <div className="max-w-sm">
                  <Label className="text-white mb-2 block">Código del cupón</Label>
                  <Input
                    value={form.coupon_code}
                    onChange={(e) => set("coupon_code", e.target.value.toUpperCase())}
                    placeholder="CONTAFLIX40"
                    className="bg-white/5 border-white/10 text-white uppercase"
                    data-testid="promo-coupon-input"
                  />
                </div>
              )}
            </div>

            {/* Paso 3 */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <p className="text-[#E50914] text-sm font-bold uppercase tracking-wider">Paso 3 · Cuándo y cuántas veces</p>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Inicio</Label>
                  <Input type="datetime-local" value={form.starts_at}
                    onChange={(e) => set("starts_at", e.target.value)}
                    className="bg-white/5 border-white/10 text-white" data-testid="promo-starts-input" />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Fin</Label>
                  <Input type="datetime-local" value={form.ends_at}
                    onChange={(e) => set("ends_at", e.target.value)}
                    className="bg-white/5 border-white/10 text-white" data-testid="promo-ends-input" />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Usos máximos (0 = ilimitado)</Label>
                  <Input type="number" min="0" value={form.max_uses}
                    onChange={(e) => set("max_uses", e.target.value)}
                    className="bg-white/5 border-white/10 text-white" data-testid="promo-max-uses-input" />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Usos por usuario</Label>
                  <Input type="number" min="1" value={form.max_uses_per_user}
                    onChange={(e) => set("max_uses_per_user", e.target.value)}
                    className="bg-white/5 border-white/10 text-white" data-testid="promo-max-uses-user-input" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Estado</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="promo-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Borrador</SelectItem>
                      <SelectItem value="active">Activa</SelectItem>
                      <SelectItem value="scheduled">Programada</SelectItem>
                      <SelectItem value="paused">Pausada</SelectItem>
                      <SelectItem value="disabled">Desactivada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.show_countdown}
                      onCheckedChange={(v) => set("show_countdown", v)}
                      data-testid="promo-countdown-switch"
                    />
                    <span className="text-white text-sm">Mostrar banner con contador en el sitio</span>
                  </div>
                </div>
              </div>

              {form.show_countdown && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block">Título del banner</Label>
                    <Input value={form.banner_title} onChange={(e) => set("banner_title", e.target.value)}
                      placeholder="¡Oferta por tiempo limitado!"
                      className="bg-white/5 border-white/10 text-white" data-testid="promo-banner-title-input" />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Descripción del banner</Label>
                    <Input value={form.banner_description} onChange={(e) => set("banner_description", e.target.value)}
                      placeholder="Aprovecha antes que termine"
                      className="bg-white/5 border-white/10 text-white" data-testid="promo-banner-desc-input" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-white mb-2 block">Notas internas (opcional)</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  className="bg-white/5 border-white/10 text-white" rows={2} data-testid="promo-notes-input" />
              </div>
            </div>

            {/* Vista previa */}
            {preview?.rows?.length > 0 && (
              <div className="border-t border-white/10 pt-6" data-testid="promo-preview">
                <p className="text-white font-semibold flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-[#E50914]" />
                  Vista previa
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {preview.rows.map((r) => (
                    <div key={r.plan} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`promo-preview-${r.plan}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-semibold">Plan {r.plan_label}</span>
                        <Badge className="bg-[#E50914] text-white border-0">{r.discount_label}</Badge>
                      </div>
                      <p className="text-white/50 text-sm">
                        Precio normal: <span className="line-through">${r.normal_price.toFixed(2)}</span>
                      </p>
                      <p className="text-2xl font-black text-white">${r.final_price.toFixed(2)}</p>
                      <p className="text-emerald-400 text-xs">Ahorro: ${r.savings.toFixed(2)}</p>
                      <p className="text-white/60 text-sm mt-2">Duración: {r.duration}</p>
                      <p className="text-white/60 text-sm">Beneficio adicional: {r.extra_benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={save} disabled={saving} className="bg-[#E50914] hover:bg-[#E50914]/90 text-white" data-testid="promo-save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Crear promoción"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Lista */}
      <Card className="bg-card/50 backdrop-blur-md border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#E50914]" />
            Promociones ({campaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-white/60 text-center py-8">Cargando...</p>
          ) : campaigns.length === 0 ? (
            <p className="text-white/60 text-center py-8">Aún no has creado promociones</p>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c) => (
                <div key={c.id} className="p-4 bg-white/5 rounded-lg border border-white/10" data-testid={`promo-item-${c.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold">{c.name}</h3>
                        <Badge className={`${STATUS_STYLES[c.status] || "bg-white/10 text-white/60"} border-0`}>
                          {c.state_label}
                        </Badge>
                        {c.preview?.objective_label && (
                          <Badge className="bg-[#E50914]/20 text-[#E50914] border-0">{c.preview.objective_label}</Badge>
                        )}
                        {c.coupon_code && (
                          <Badge className="bg-blue-500/20 text-blue-300 border-0">{c.coupon_code}</Badge>
                        )}
                        {c.show_countdown && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-0">Con contador</Badge>
                        )}
                      </div>
                      <p className="text-white/60 text-sm mt-1">
                        {(c.preview?.rows || []).map((r) => `${r.plan_label}: $${r.normal_price.toFixed(2)} → $${r.final_price.toFixed(2)} (${r.discount_label})`).join(" · ")}
                      </p>
                      <p className="text-white/40 text-xs mt-1">
                        Público: {AUDIENCES.find((a) => a.value === c.audience)?.label} · Usos: {c.uses_count || 0}
                        {c.max_uses ? `/${c.max_uses}` : " (ilimitado)"} · Por usuario: {c.max_uses_per_user} ·{" "}
                        {c.combinable ? "Combinable" : "No combinable"}
                        {c.ends_at && ` · Fin: ${new Date(c.ends_at).toLocaleString("es-EC")}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.status !== "active" && (
                        <Button onClick={() => changeStatus(c, "active")} className="bg-green-600 hover:bg-green-600/90 text-white" data-testid={`promo-activate-${c.id}`}>
                          <Play className="w-4 h-4 mr-1" /> Activar
                        </Button>
                      )}
                      {c.status === "active" && (
                        <Button onClick={() => changeStatus(c, "paused")} className="bg-amber-600 hover:bg-amber-600/90 text-white" data-testid={`promo-pause-${c.id}`}>
                          <Pause className="w-4 h-4 mr-1" /> Pausar
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => changeStatus(c, "disabled")} className="border-white/20 text-white hover:bg-white/10" data-testid={`promo-disable-${c.id}`}>
                        <Ban className="w-4 h-4 mr-1" /> Desactivar
                      </Button>
                      <Button variant="outline" onClick={() => startEdit(c)} className="border-white/20 text-white hover:bg-white/10" data-testid={`promo-edit-${c.id}`}>
                        <Edit2 className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <Button variant="outline" onClick={() => remove(c)} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`promo-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historial de cambios */}
      <Card className="bg-card/50 backdrop-blur-md border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-[#E50914]" />
            Historial de cambios ({audit.length})
          </CardTitle>
          <CardDescription className="text-white/60">Registro de acciones realizadas por los administradores.</CardDescription>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-white/60 text-center py-6">Sin movimientos aún</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 p-3 bg-white/5 rounded-lg border border-white/10" data-testid={`promo-audit-${a.id}`}>
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{a.campaign_name} — {a.detail}</p>
                    <p className="text-white/40 text-xs truncate">{a.admin_name}</p>
                  </div>
                  <p className="text-white/50 text-xs shrink-0">{new Date(a.created_at).toLocaleString("es-EC")}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PromotionsModule;
